import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { LoadingScreen } from "@/components/common/loading-screen";
import { RouteErrorPage } from "@/components/common/route-error-page";
import { AppShell } from "@/components/layout/app-shell";
import { GroupLayout } from "@/components/layout/group-layout";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { NotFoundPage, UnauthorizedPage } from "@/pages/error-pages";
import { LandingPage } from "@/pages/landing-page";
import { LoginPage } from "@/pages/login-page";

const DashboardPage = lazy(() =>
  import("@/pages/dashboard-page").then((module) => ({
    default: module.DashboardPage,
  })),
);
const GroupOverviewPage = lazy(() =>
  import("@/pages/group-overview-page").then((module) => ({
    default: module.GroupOverviewPage,
  })),
);
const DocumentationPageView = lazy(() =>
  import("@/pages/documentation-page").then((module) => ({
    default: module.DocumentationPageView,
  })),
);
const GroupSettingsPage = lazy(() =>
  import("@/pages/group-settings-page").then((module) => ({
    default: module.GroupSettingsPage,
  })),
);
const GroupMembersPage = lazy(() =>
  import("@/pages/group-members-page").then((module) => ({
    default: module.GroupMembersPage,
  })),
);
const GroupActivityPage = lazy(() =>
  import("@/pages/group-activity-page").then((module) => ({
    default: module.GroupActivityPage,
  })),
);
const GroupTrashPage = lazy(() =>
  import("@/pages/group-trash-page").then((module) => ({
    default: module.GroupTrashPage,
  })),
);
const SearchPage = lazy(() =>
  import("@/pages/search-page").then((module) => ({
    default: module.SearchPage,
  })),
);
const PersonalPages = lazy(() =>
  import("@/pages/personal-pages").then((module) => ({
    default: module.PersonalPages,
  })),
);
const UserInvitationsPage = lazy(() =>
  import("@/pages/invitations-page").then((module) => ({
    default: module.UserInvitationsPage,
  })),
);
const GroupInvitationsPage = lazy(() =>
  import("@/pages/invitations-page").then((module) => ({
    default: module.GroupInvitationsPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/pages/profile-page").then((module) => ({
    default: module.ProfilePage,
  })),
);

function deferred(element: ReactNode) {
  return <Suspense fallback={<LoadingScreen />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: "/", element: <LandingPage /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/unauthorized", element: <UnauthorizedPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/app",
            element: <AppShell />,
            children: [
              { index: true, element: deferred(<DashboardPage />) },
              { path: "search", element: deferred(<SearchPage />) },
              {
                path: "favorites",
                element: deferred(<PersonalPages kind="favorites" />),
              },
              {
                path: "recent",
                element: deferred(<PersonalPages kind="recent" />),
              },
              {
                path: "invitations",
                element: deferred(<UserInvitationsPage />),
              },
              { path: "profile", element: deferred(<ProfilePage />) },
              {
                path: "groups/:groupId",
                element: <GroupLayout />,
                children: [
                  { index: true, element: deferred(<GroupOverviewPage />) },
                  {
                    path: "pages/:pageId",
                    element: deferred(<DocumentationPageView />),
                  },
                  {
                    path: "activity",
                    element: deferred(<GroupActivityPage />),
                  },
                  { path: "trash", element: deferred(<GroupTrashPage />) },
                  {
                    path: "settings",
                    element: deferred(<GroupSettingsPage />),
                  },
                  {
                    path: "settings/members",
                    element: deferred(<GroupMembersPage />),
                  },
                  {
                    path: "settings/invitations",
                    element: deferred(<GroupInvitationsPage />),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
